import { Image } from "expo-image";
import React from "react";
import { Pressable, View } from "react-native";

import { Text } from "./ui/BrandText";
import { border, brand, surface, text as uiText } from "../theme/tokens";
import { avatarSourceFor } from "../theme/defaultAvatars";
import { distinctIdentityHandle } from "../lib/identityPresentation";
import type { IdentityBadge, PublicIdentitySnapshot } from "../types/identity";

function badgeStyle(badge: IdentityBadge) {
  switch (badge.kind) {
    case "teacher_verified":
      return { color: brand.primary, backgroundColor: surface.selected, borderColor: brand.primary };
    case "teacher_premium":
      return { color: brand.accent, backgroundColor: surface.warning, borderColor: brand.accent };
    case "institution":
    case "institution_premium":
      return { color: brand.primary, backgroundColor: surface.selected, borderColor: brand.primary };
    case "teacher":
      return { color: brand.accent, backgroundColor: surface.warning, borderColor: brand.accent };
    case "student":
    default:
      return { color: uiText.info, backgroundColor: surface.info, borderColor: brand.secondary };
  }
}

function badgeDisplayLabel(badge: IdentityBadge) {
  switch (badge.kind) {
    case "institution":
      return "École";
    case "institution_premium":
      return "École premium";
    default:
      return badge.label;
  }
}

export function IdentityHeader({
  identity,
  onPress,
  disabled = false,
  compact = false,
  showHandle = true,
  pressableIdentityOnly = false,
  nameTrailingContent,
}: {
  identity: PublicIdentitySnapshot;
  onPress?: () => void;
  disabled?: boolean;
  compact?: boolean;
  showHandle?: boolean;
  pressableIdentityOnly?: boolean;
  nameTrailingContent?: React.ReactNode;
}) {
  const displayName = identity.displayName.trim() || identity.handle.trim() || "Utilisateur";
  const handle = distinctIdentityHandle(displayName, identity.handle);
  const avatarSource = avatarSourceFor(identity.avatarUrl, identity.identityId || displayName);
  const avatar = (
    <Image source={avatarSource} cachePolicy="disk" contentFit="cover" accessibilityLabel={`Avatar de ${displayName}`} style={{ width: compact ? 40 : 48, height: compact ? 40 : 48, borderRadius: 999, backgroundColor: surface.overlay, borderWidth: 1, borderColor: border.brand, flexShrink: 0 }} />
  );
  const name = (
    <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: compact ? 15 : 17, flexShrink: 1 }}>{displayName}</Text>
  );
  const identityPressableStyle = ({ pressed }: { pressed: boolean }) => ({
    opacity: disabled ? 0.55 : pressed ? 0.75 : 1,
  });
  const interactiveAvatar = onPress && pressableIdentityOnly ? (
    <Pressable onPress={onPress} disabled={disabled} accessibilityRole="button" accessibilityLabel={`Ouvrir l'identité publique de ${displayName}`} style={identityPressableStyle}>
      {avatar}
    </Pressable>
  ) : avatar;
  const interactiveName = onPress && pressableIdentityOnly ? (
    <Pressable onPress={onPress} disabled={disabled} accessibilityRole="button" accessibilityLabel={`Ouvrir l'identité publique de ${displayName}`} style={identityPressableStyle}>
      {name}
    </Pressable>
  ) : name;
  const content = (
    <View>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: compact ? 10 : 12 }}>
        {interactiveAvatar}
        <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
            {interactiveName}
            {(identity.badges ?? []).slice(0, 2).map((badge) => {
              const colors = badgeStyle(badge);
              return (
                <View key={`${badge.kind}:${badge.label}`} style={{ paddingHorizontal: 7, paddingVertical: 3, borderRadius: 999, backgroundColor: colors.backgroundColor, borderWidth: 1, borderColor: colors.borderColor, maxWidth: "100%" }}>
                  <Text numberOfLines={1} style={{ color: colors.color, fontWeight: "900", fontSize: 11 }}>{badgeDisplayLabel(badge)}</Text>
                </View>
              );
            })}
            {nameTrailingContent}
          </View>
          {showHandle && handle ? <Text style={{ color: uiText.muted, fontWeight: "700", fontSize: compact ? 12 : 13, flexShrink: 1 }}>@{handle}</Text> : null}
        </View>
      </View>
    </View>
  );

  if (!onPress || pressableIdentityOnly) return content;
  return (
    <Pressable onPress={onPress} disabled={disabled} accessibilityRole="button" accessibilityLabel={`Ouvrir l'identité publique de ${displayName}`} style={({ pressed }) => ({ opacity: disabled ? 0.55 : pressed ? 0.75 : 1 })}>
      {content}
    </Pressable>
  );
}
